import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { assertIsClientContext, context } from '@adornis/baseql/server/context.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { InvalidUserIDError } from '@adornis/users/db/errors.js';
import { setUserOnContext } from '@adornis/users/server/baseqlContextHelper.js';
import { changeUserPassword } from '@adornis/users/server/password.js';
import { assertNexusUserTargetPermission } from '@nsc/nexus-permissions/nexus-api/nexus-permission-gate.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';

export const setNexusUserPassword = registerMutation({
  operationName: 'setNexusUserPassword',
  type: () => Boolean,
  params: [
    { name: 'userId', type: () => String },
    { name: 'password', type: () => String },
  ],
  resolve: (userId: string, password: string) => async () => {
    await assertNexusUserTargetPermission(NexusPermission.UsersChangePassword, userId, 'set user password');

    const collection = await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName);
    const user = await collection.findOne({ _id: userId }, { projection: { _id: 1 } });
    if (!user?._id) throw new InvalidUserIDError();

    AdornisUser.passwordValidator({ value: password, key: 'password', target: undefined as never });
    await changeUserPassword(userId, password, AdornisUser);
    return true;
  },
});

export const nexusImpersonateUser = registerMutation({
  operationName: 'nexusImpersonateUser',
  type: () => String,
  params: [{ name: 'userId', type: () => String }],
  resolve: (userId: string) => async (): Promise<string> => {
    await assertNexusUserTargetPermission(NexusPermission.UsersImpersonate, userId, 'impersonate user');

    assertIsClientContext(context);
    const collection = await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName);
    const target = await collection.findOne({ _id: userId }, { projection: { _id: 1, _class: 1 } });
    if (!target?._id) throw new InvalidUserIDError();

    const targetId = String(target._id);
    const targetClass = target._class ?? AdornisUser._class;
    context.impersonatingUser = context.userID;
    context.impersonatingUserClass = context.userClass;
    await setUserOnContext({ _id: targetId, _class: targetClass });
    return targetId;
  },
});
